/** Keep consent-permitted startup errors while the monitoring chunk loads. */
type Monitoring = Pick<typeof import('@sentry/nextjs'), 'captureException' | 'captureRouterTransitionStart'>;
type Transition = Parameters<Monitoring['captureRouterTransitionStart']>;
type Pending = { error: unknown; mechanism: 'onerror' | 'onunhandledrejection' } | { transition: Transition };

export function createBrowserMonitoring(
  load: () => Promise<Monitoring>,
  allowed: () => boolean,
  target: Pick<Window, 'addEventListener' | 'removeEventListener'>,
) {
  let monitoring: Monitoring | undefined;
  let loading: Promise<void> | undefined;
  const pending: Pending[] = [];
  const clearPending = () => { pending.length = 0; };
  const enqueue = (item: Pending) => {
    if (allowed()) pending.push(item);
    else clearPending();
  };
  const onError = (event: ErrorEvent) => {
    if (event.error || event.message) enqueue({ error: event.error || event.message, mechanism: 'onerror' });
  };
  const onRejection = (event: PromiseRejectionEvent) => enqueue({ error: event.reason, mechanism: 'onunhandledrejection' });
  const removeListeners = () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
  };
  const send = (item: Pending) => {
    if (!monitoring || !allowed()) return;
    if ('transition' in item) monitoring.captureRouterTransitionStart(...item.transition);
    else monitoring.captureException(item.error, { mechanism: { type: item.mechanism, handled: false } });
  };
  const start = (): Promise<void> => {
    if (!allowed() || monitoring) return Promise.resolve();
    if (loading) return loading;
    target.addEventListener('error', onError);
    target.addEventListener('unhandledrejection', onRejection);
    loading = load().then(sdk => {
      monitoring = sdk;
      removeListeners();
      for (const item of pending) send(item);
      clearPending();
    }).catch(() => {
      // A blocked monitoring asset cannot reject the application bootstrap.
      removeListeners();
      clearPending();
    }).finally(() => { loading = undefined; });
    return loading;
  };
  return {
    start,
    clearPending,
    onRouterTransitionStart(...transition: Transition) {
      if (!allowed()) { clearPending(); return; }
      if (monitoring) send({ transition });
      else { enqueue({ transition }); void start(); }
    },
  };
}
